import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

i18n
  // Detect user language
  .use(LanguageDetector)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize i18next
  .init({
    debug: process.env.NODE_ENV === 'development',
    fallbackLng: 'es',
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    resources: {
      en: {
        translation: {
          unsavedChanges: {
            title: 'Unsaved changes',
            message: 'You have unsaved changes. If you leave now they will be lost.',
            keepEditing: 'Keep editing',
            discardAndLeave: 'Discard changes and leave',
          },
          common: {
            loading: 'Loading...',
            error: 'An error occurred',
            save: 'Save',
            cancel: 'Cancel',
            close: 'Close',
            imageUpload: {
              upload: 'Upload image',
              change: 'Change image',
              remove: 'Remove image',
              dragDrop: 'Drag and drop an image here, or browse from your device.',
              help: 'The app will generate thumb, card, and detail variants in the browser.',
              optimizing: 'Optimizing image...',
              optimized: 'Optimized: {{name}}',
              previewAlt: 'Image preview',
              placeholder: 'No image selected yet',
              variants: 'Thumb / card / detail',
              error: 'Could not optimize selected image.'
            },
            search: 'Search',
            actions: 'Actions',
            delete: 'Delete',
            edit: 'Edit',
            create: 'Create',
            update: 'Update',
            refresh: 'Refresh',
            add: 'Add',
            remove: 'Remove',
            back: 'Back',
            next: 'Next',
            previous: 'Previous',
            yes: 'Yes',
            no: 'No',
            filter: 'Filter',
            clear: 'Clear',
            submit: 'Submit',
            confirm: 'Confirm',
            noData: 'No data available',
            selectAll: 'Select All',
            optional: 'Optional',
            required: 'Required',
            deleting: 'Deleting...'
          },
          pagination: {
            show: 'Show',
            itemsPerPage: 'items per page',
            showing: 'Showing',
            of: 'of'
          },
          navbar: {
            brand: 'Waggy ERP',
            hello: 'Hello',
            login: 'Login',
            logout: 'Logout',
            admin: 'Admin',
            adminUserManagement: 'User Management',
            adminContacts: 'Contact Messages',
            adminPlatformTenants: 'Platform Tenants'
          },
          sidebar: {
            inventory: 'Inventory',
            pets: 'Pets',
            sales: 'Sales',
            invoices: 'Invoices',
            daycare: 'Daycare',
            grooming: 'Grooming',
            platform: 'Platform Tenants',
            storefrontOrders: 'Storefront Orders',
            storefrontCustomers: 'Storefront Customers',
            navigation: 'Navigation',
            toggleMenu: 'Toggle navigation menu',
            closeMenu: 'Close navigation menu'
          },
          storefront: {
            status: {
              PENDING_PAYMENT: 'Pending payment',
              PAID: 'Paid',
              FULFILLED: 'Fulfilled',
              CANCELLED: 'Cancelled',
            },
            paymentStatus: {
              PENDING: 'Pending',
              REQUIRES_ACTION: 'Requires action',
              PROCESSING: 'Processing',
              PAID: 'Paid',
              FAILED: 'Failed',
              REFUNDED: 'Refunded',
            },
            orders: {
              title: 'Storefront Orders',
              subtitle: 'Orders placed through the public storefront',
              orderNumber: 'Order',
              customer: 'Customer',
              status: 'Status',
              total: 'Total',
              placedAt: 'Placed',
              allStatuses: 'All statuses',
              searchPlaceholder: 'Search order number, email or name',
              empty: 'No storefront orders yet',
              loadFailed: 'Failed to load orders',
              actionFailed: 'Action failed',
              viewDetails: 'View details',
              detailTitle: 'Order details',
              items: 'Items',
              subtotal: 'Subtotal',
              vat: 'VAT',
              shipping: 'Shipping',
              shippingAddress: 'Shipping address',
              notes: 'Notes',
              markFulfilled: 'Mark as fulfilled',
              cancelOrder: 'Cancel order',
              fulfilSuccess: 'Order marked as fulfilled',
              cancelSuccess: 'Order cancelled and stock released',
              confirmFulfilTitle: 'Mark order as fulfilled?',
              confirmFulfilMessage: 'The customer will see this order as fulfilled.',
              confirmCancelTitle: 'Cancel this order?',
              confirmCancelMessage: 'The reserved stock will be released back into inventory.',
            },
            customers: {
              title: 'Storefront Customers',
              subtitle: 'Customer accounts registered on the public storefront',
              name: 'Name',
              phone: 'Phone',
              orders: 'Orders',
              lifetimeValue: 'Lifetime value',
              status: 'Status',
              active: 'Active',
              inactive: 'Inactive',
              joined: 'Joined',
              searchPlaceholder: 'Search by name or email',
              empty: 'No storefront customers yet',
              loadFailed: 'Failed to load customers',
            },
          },
          widget: {
            admin: {
              title: 'Widget Submissions',
              subtitle: 'Contact messages and grooming appointment requests from your public widget',
              empty: 'No widget submissions yet.',
              statusUpdated: 'Status updated.',
              markRead: 'Mark as read',
              markHandled: 'Mark as handled',
              col: {
                type: 'Type',
                status: 'Status',
                name: 'Name',
                email: 'Email',
                pet: 'Pet',
                preferred: 'Preferred date / time',
                message: 'Message',
                date: 'Received',
              },
              error: {
                loadFailed: 'Failed to load widget submissions.',
                updateFailed: 'Failed to update submission status.',
              },
            },
            settings: {
              slugTitle: 'Your widget slug',
              slugDesc: 'This is your unique identifier in the embed URL. Share the iframe code below on your own website so customers can contact you.',
              slugLoading: 'Loading…',
              embedTitle: 'Embed code',
              embedDesc: 'Copy this snippet and paste it into any page on your website.',
              copy: 'Copy',
              copied: 'Copied!',
              previewTitle: 'Live preview',
              previewDesc: 'This is how the widget looks on your customers\' screens.',
            },
          },
          inventory: {
            title: 'Inventory Management',
            searchPlaceholder: 'Search by name, SKU, category…',
            barCodePlaceholder: 'Search by barcode…',
            showInactive: 'Show Inactive',
            addProduct: 'Add Product',
            actions: 'Actions',
            columns: {
              name: 'Name',
              sku: 'SKU',
              brand: 'Brand',
              category: 'Category',
              stock: 'Stock',
              finalPrice: 'Final Price',
              status: 'Status',
              actions: 'Actions'
            },
            filters: {
              category: 'Category',
              allCategories: 'All categories',
              brand: 'Brand',
              allBrands: 'All brands'
            },
            alerts: {
              title: 'Alerts',
              empty: 'No alerts',
              colName: 'Name',
              colStatus: 'Status'
            },
            purchaseHistory: {
              title: 'Purchase History',
              loadFailed: 'Failed to load purchase history',
              addFailed: 'Failed to add purchase',
              removeFailed: 'Failed to remove purchase',
              unitPricePlaceholder: 'New price',
              supplierPlaceholder: 'Supplier',
              notesPlaceholder: 'Notes',
              add: 'Add purchase',
              saving: 'Saving…',
              loading: 'Loading…',
              empty: 'No purchases recorded',
              colDate: 'Date',
              colPrice: 'Price',
              colChange: 'Change',
              colSupplier: 'Supplier',
              colNotes: 'Notes',
              remove: 'Remove purchase'
            },
            startSale: 'Start Sale',
            viewDetails: 'View details',
            storefront: 'Storefront',
            published: 'Published',
            hidden: 'Hidden',
            publish: 'Publish to storefront',
            unpublish: 'Hide from storefront',
            publishSuccess: 'Product published to the storefront',
            unpublishSuccess: 'Product hidden from the storefront',
            publishFailed: 'Failed to update storefront visibility',
            duplicateProduct: 'Duplicate',
            createProduct: 'Create Product',
            editProduct: 'Edit Product',
            productDetails: 'Product Details',
            productName: 'Product Name',
            description: 'Description',
            sku: 'SKU',
            barcode: 'Barcode',
            brand: 'Brand',
            category: 'Category',
            categoryCode: 'Category Code',
            categoryName: 'Category Name',
            purchasePrice: 'Purchase Price',
            sellPrice: 'Sell Price',
            stock: 'Stock',
            minStock: 'Min Stock',
            notes: 'Notes',
            active: 'Active',
            namePlaceholder: 'Enter product name',
            descriptionPlaceholder: 'Enter product description',
            skuPlaceholder: 'Product SKU',
            barcodePlaceholder: 'Barcode (optional)',
            brandPlaceholder: 'Brand (optional)',
            categoryCodePlaceholder: 'Category code (optional)',
            categoryNamePlaceholder: 'Category name (optional)',
            purchasePricePlaceholder: '0.00',
            sellPricePlaceholder: '0.00',
            stockPlaceholder: '0',
            minStockPlaceholder: 'Optional',
            weightPlaceholder: 'Optional',
            notesPlaceholder: 'Optional',
            productModal: {
              titleCreate: 'Create New Product',
              titleEdit: 'Edit Product',
              titleDuplicate: 'Duplicate Product',
              sections: {
                basic: 'Basic Information',
                pricing: 'Pricing',
                inventory: 'Inventory'
              },
              fields: {
                listPrice: 'Base Price (VAT excluded)',
                finalPrice: 'Sale Price (PVP, VAT included)',
                vatRate: 'VAT Rate',
                costPrice: 'Cost Price',
                stockQuantity: 'Stock Quantity',
                minStockLevel: 'Minimum Stock Level',
                weightGrams: 'Weight (g)',
                daycareTime: 'Daycare Time',
                daycareBundle: 'Daycare Bundle'
              },
              image: {
                label: 'Product Image',
                help: 'Generates thumb/card/detail variants in browser and sends them with the same create/update payload.',
                optimizing: 'Optimizing image...',
                optimized: 'Optimized: {{name}}',
                previewAlt: 'Product preview'
              },
              selectBrand: 'Select brand',
              selectCategory: 'Select category',
              selectDaycareTime: 'Select daycare time',
              actions: {
                saving: 'Saving...',
                update: 'Update Product',
                create: 'Create Product',
                duplicate: 'Duplicate Product'
              },
              errors: {
                saveFailed: 'Failed to save product',
                unexpected: 'An unexpected error occurred'
              }
            },
            importExport: {
              exportProducts: 'Export Products',
              importProducts: 'Import Products',
              exporting: 'Exporting...',
              importing: 'Importing...',
              exportFailed: 'Failed to export products',
              importFailed: 'Failed to import products',
              summary: 'Import complete. Created: {{created}}, Updated: {{updated}}, Skipped: {{skipped}}, Failed: {{failed}}'
            }
          },
          category: {
            confirm: {
              management: 'Category Management',
              deleteTitle: 'Delete category',
              delete: 'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
            },
          },
          brand: {
            management: 'Brand Management',
            title: 'Brands',
            create: 'Create Brand',
            edit: 'Edit Brand',
            delete: 'Delete Brand',
            field: {
              name: 'Brand Name',
              description: 'Description'
            },
            placeholder: {
              name: 'Enter brand name',
              description: 'Enter brand description (optional)'
            },
            empty: 'No brands available',
            confirm: {
              deleteTitle: 'Delete brand',
              delete: 'Are you sure you want to delete "{{name}}"? This action cannot be undone.'
            },
            error: {
              loadFailed: 'Failed to load brands',
              saveFailed: 'Failed to save brand',
              deleteFailed: 'Failed to delete brand',
              nameRequired: 'Brand name is required',
              nameTooLong: 'Brand name cannot exceed 100 characters',
              descriptionTooLong: 'Brand description cannot exceed 500 characters'
            },
            success: {
              created: 'Brand created successfully',
              updated: 'Brand updated successfully',
              deleted: 'Brand deleted successfully'
            }
          },
          productGroup: {
            management: 'Variations',
            title: 'Product Variations',
            create: 'Create Variation Group',
            edit: 'Edit Variation Group',
            empty: 'No variation groups yet. Group products to sell them as one card with options.',
            loadFailed: 'Failed to load variation groups',
            saveFailed: 'Failed to save the variation group',
            deleteFailed: 'Failed to delete the variation group',
            defaultVariation: 'Default',
            confirm: {
              deleteTitle: 'Delete variation group',
              delete: 'Delete variation group {{name}}? The products themselves are kept and go back to being sold on their own.'
            },
            field: {
              name: 'Group Name',
              description: 'Description',
              isActive: 'Active',
              slug: 'Storefront URL',
              priceRange: 'Price range',
              totalStock: 'Total stock',
              productCount: 'Variations',
              attributeCode: 'Code',
              attributeName: 'Option name',
              valueCode: 'Code',
              valueName: 'Value',
              hexColor: 'Swatch colour',
              product: 'Product',
              productSku: 'SKU',
              productPrice: 'Price',
              productStock: 'Stock'
            },
            placeholder: {
              name: 'e.g. Arnés Clásico Dukier',
              description: 'Optional description shown on the storefront',
              attributeName: 'e.g. Tamaño, Color',
              attributeCode: 'e.g. tamano',
              valueName: 'e.g. S, M, L, Azul',
              valueCode: 'e.g. s, m, l, azul',
              product: 'Select a product'
            },
            search: {
              product: 'Search by name, SKU, category…',
              noProductsFound: 'No products match',
              inOtherGroup: 'In group {{group}}',
              inThisGroup: 'Already in this group',
              clearProduct: 'Remove product'
            },
            section: {
              details: 'Group details',
              attributes: 'Options',
              members: 'Variations'
            },
            action: {
              addAttribute: 'Add option',
              addValue: 'Add value',
              addMember: 'Add variation',
              removeAttribute: 'Remove option',
              removeValue: 'Remove value',
              removeMember: 'Remove variation',
              setDefault: 'Make default'
            },
            helper: {
              attributes: 'Define the option types shoppers pick from, such as size or colour. Each variation selects one value per option.',
              members: 'Each variation is a real product with its own SKU, barcode, price, stock and images. A product can belong to only one group.',
              codes: 'Codes are sent explicitly. They start pre-filled from the name; change them if you need a different value.',
              priceRange: '{{min}} – {{max}}',
              singlePrice: '{{price}}'
            },
            error: {
              nameRequired: 'Group name is required',
              noMembers: 'A group must have at least one variation',
              attributeNameRequired: 'Every option needs a name',
              attributeCodeRequired: 'Give the option a name so its code can be filled in',
              attributeValueRequired: 'Every option needs at least one value',
              valueNameRequired: 'Every value needs a name',
              duplicateValueCode: 'Duplicate value code "{{code}}"',
              duplicateAttributeCode: 'Duplicate option code "{{code}}"',
              invalidHexColor: '"{{color}}" is not a valid hex colour',
              productRequired: 'Every variation needs a product',
              duplicateProduct: 'The same product is listed more than once',
              missingSelection: 'Variation {{index}} is missing a value for {{attribute}}',
              unknownSelection: '"{{value}}" is not a valid value for {{attribute}}',
              defaultNotMember: 'The default variation must be one of the group variations'
            }
          },
          pets: {
            title: 'Pet Management',
            createPet: 'Create Pet',
            editPet: 'Edit Pet',
            deletePet: 'Delete Pet',
            modal: {
              titleCreate: 'Create New Pet',
              titleEdit: 'Edit Pet',
              sections: {
                petInfo: 'Pet Information',
                ownerInfo: 'Owner Information'
              },
              selectCustomer: 'Select Customer',
              selectCustomerPlaceholder: '-- Select a customer --',
              selectExistingCustomer: 'Select Existing Customer',
              createNewCustomer: 'Create New Customer',
              actions: {
                saving: 'Saving...',
                update: 'Update Pet'
              },
              errors: {
                saveFailed: 'Failed to save pet',
                unexpected: 'An unexpected error occurred',
                createCustomerFailed: 'Failed to create customer',
                customerRequired: 'Please select a customer or create a new one'
              }
            },
            image: {
              label: 'Pet Image',
              help: 'Upload one pet photo and the app will generate the same thumb, card, and detail variants used by products.',
              previewAlt: 'Pet preview'
            },
            viewDetails: 'View Details',
            petDetails: 'Pet Details',
            petName: 'Pet Name',
            breed: 'Breed',
            birthDate: 'Birth Date',
            gender: 'Gender',
            weight: 'Weight',
            color: 'Color',
            notes: 'Notes',
            owner: 'Owner',
            customerInfo: 'Customer Information',
            customerName: 'Customer Name',
            email: 'Email',
            phone: 'Phone',
            address: 'Address',
            namePlaceholder: 'Enter pet name',
            breedPlaceholder: 'Enter pet race or breed',
            customerNamePlaceholder: 'Enter customer name',
            emailPlaceholder: 'customer@example.com',
            phonePlaceholder: '+1 234 567 8900',
            addressPlaceholder: 'Full address',
            searchPlaceholder: 'Search by pet name, breed or owner…',
            empty: 'No pets found',
            selector: {
              selectExisting: 'Select Existing Pet',
              onboardNew: 'Onboard New Pet',
              existingOwner: 'Existing Owner',
              newOwner: 'New Owner',
              selectOwner: 'Select Owner',
              ownerNameLabel: 'Owner Name'
            },
            quickAddPet: 'Quick Add Pet',
            petNameQuick: 'e.g. Max',
            breedQuick: 'e.g. Golden Retriever',
            ownerNamePlaceholder: 'Full name',
            ownerEmailPlaceholder: 'owner@example.com',
            ownerPhonePlaceholder: '+1 234 567 8900',
            ownerAddressPlaceholder: 'Full address',
            detailsModal: {
              inDaycareSince: 'In Daycare since {{time}}',
              loadingHistory: 'Loading purchase history…',
              emptyHistory: 'No purchases recorded for {{petName}} yet.',
              loadingDaycare: 'Loading daycare balance…',
              emptyDaycare: 'No daycare hours purchased yet.',
              remainingUnits: 'Remaining daycare units',
              daycareSessionsTitle: 'Daycare sessions for this dog',
              daycareSessionsEmpty: 'No daycare sessions recorded for this dog yet.',
              daycareSessionFallback: 'Daycare session',
              inProgress: 'In progress',
              sections: {
                topProducts: 'Most Purchased Products',
                daycareHours: 'Daycare Hours',
                orderHistory: 'Order History',
                sessionSummary: 'Session Summary'
              },
              stats: {
                totalOrders: 'Total Orders',
                totalSpent: 'Total Spent',
                avgOrder: 'Avg. Order',
                uniqueProducts: 'Unique Products'
              },
              labels: {
                checkIn: 'Check-in',
                checkOut: 'Check-out',
                entry: 'Entry',
                exit: 'Exit',
                duration: 'Duration',
                hoursToDeduct: 'Hours to deduct',
                balanceAfterSession: 'Balance after session'
              },
              status: {
                active: 'ACTIVE',
                completed: 'COMPLETED'
              },
              itemLabel: {
                singular: 'item',
                plural: 'items'
              },
              table: {
                product: 'Product',
                unitPrice: 'Unit Price',
                qty: 'Qty',
                subtotal: 'Subtotal'
              },
              actions: {
                confirmEntryTime: '🐾 Confirm Dog Entry Time',
                confirmExitTime: '🏠 Confirm Dog Exit Time',
                confirmEntry: 'Confirm Entry',
                confirmExit: 'Confirm Exit',
                reset: 'Reset',
                recording: 'Recording…',
                recordCheckout: 'Record Check-out',
                dogEntry: 'Dog Entry',
                dogExit: 'Dog Exit'
              },
              summary: {
                sufficientBalance: '{{petName}} has sufficient balance. {{remaining}} will remain after this session.',
                insufficientBalance: 'Insufficient balance. This will leave {{petName}} with an overdraft of {{overdraft}}.'
              },
              errors: {
                loadHistoryFailed: 'Failed to load purchase history',
                checkInFailed: 'Failed to record check-in',
                checkOutFailed: 'Failed to record check-out',
                invalidExitTime: 'Exit time must be after check-in time. Please correct the times.'
              }
            }
          },
          sales: {
            title: 'Sales',
            subtitle: 'All orders sorted by most recent sale',
            invoices: {
              title: 'Invoices',
              subtitle: 'Issued invoices and exports',
              export: 'Export Excel',
              exporting: 'Exporting…',
              empty: 'No invoices found',
              colNumber: 'Invoice #',
              colIssuedAt: 'Issued At',
              colCustomer: 'Customer',
              colPet: 'Pet'
            },
            newSale: 'New Sale',
            createSale: 'Create Sale',
            orderDate: 'Order Date',
            total: 'Total',
            status: 'Status',
            items: 'Items',
            customer: 'Customer',
            selectPet: 'Select Pet',
            selectProduct: 'Select Product',
            quantity: 'Quantity',
            price: 'Price',
            addItem: 'Add Item',
            searchProductPlaceholder: 'Search by name, SKU, category…',
            modal: {
              editDraft: 'Edit Draft Order',
              loading: 'Loading data...',
              addProducts: '2. Add Products',
              orderSummary: '3. Order Summary',
              emptyCart: 'No products added yet.',
              product: 'Product',
              unitPrice: 'Unit Price',
              qty: 'Qty',
              subtotal: 'Subtotal',
              cancel: 'Cancel',
              saving: 'Saving…',
              saveDraft: 'Save Draft',
              completeOrder: 'Complete Order',
              confirmSale: 'Confirm Sale'
            }
          },
          daycare: {
            title: 'Daycare',
            subtitle: 'Dogs currently in daycare are shown first, then sorted by check-out date.',
            checkIn: 'Check In',
            checkOut: 'Check Out',
            openSessions: 'Open Sessions',
            completedToday: 'Completed Today',
            totalSessions: 'Total Sessions',
            pet: 'Pet',
            owner: 'Owner',
            checkInTime: 'Check In',
            checkOutTime: 'Check Out',
            duration: 'Duration',
            status: 'Status',
            actions: 'Actions'
          },
          grooming: {
            title: 'Grooming',
            subtitle: 'Schedule and manage grooming appointments for pets.',
            newAppointment: 'New Appointment',
            createAppointment: 'Create Appointment',
            editAppointment: 'Edit Appointment',
            deleteAppointment: 'Delete Appointment',
            pet: 'Pet',
            owner: 'Owner',
            date: 'Date',
            time: 'Time',
            service: 'Service',
            status: 'Status',
            notes: 'Notes',
            notesPlaceholder: 'Optional notes about the appointment...',
            scheduledDateTime: 'Scheduled Date & Time',
            saveChanges: 'Save Changes',
            saving: 'Saving...',
            creating: 'Creating...',
            previousDay: 'Previous day',
            nextDay: 'Next day',
            clearFilter: 'Clear filter',
            edit: 'Edit',
            delete: 'Delete',
            allStatuses: 'All Statuses',
            scheduled: 'Scheduled',
            completed: 'Completed',
            cancelled: 'Cancelled'
          },
          footer: {
            language: 'Language',
            english: 'English',
            spanish: 'Español'
          },
          login: {
            title: 'Login',
            email: 'Email',
            password: 'Password',
            emailPlaceholder: 'your@email.com',
            passwordPlaceholder: 'Minimum 6 characters',
            loginButton: 'Login',
            loginError: 'Login failed. Please check your credentials.'
          },
          admin: {
            users: {
              pageTitle: 'User Management',
              createUser: 'Create User',
              editUser: 'Edit User',
              name: 'Name',
              email: 'Email',
              tenant: 'Tenant',
              roles: 'Roles',
              status: 'Status',
              actions: 'Actions',
              allTenants: 'All tenants',
              tenantFilter: 'Filter by tenant',
              superAdminScope: 'Cross-tenant user management enabled',
              loadTenantsError: 'Failed to load tenants.',
              active: 'Active',
              inactive: 'Inactive',
              edit: 'Edit',
              deactivate: 'Deactivate',
              noUsers: 'No users found.',
              loadError: 'Failed to load users.',
              created: 'User created.',
              updated: 'User updated.',
              deactivated: 'User deactivated.',
              deactivateError: 'Failed to deactivate user.',
              form: {
                name: 'Name',
                email: 'Email',
                password: 'Password',
                tenant: 'Tenant',
                selectTenant: 'Select tenant',
                roles: 'Roles',
                active: 'Active',
                nameRequired: 'Name is required.',
                emailRequired: 'Email is required.',
                passwordRequired: 'Password is required.',
                tenantRequired: 'Tenant is required.',
                rolesRequired: 'Select at least one role.',
                error: 'An error occurred. Please try again.'
              }
            }
          },
          public: {
            nav: {
              features: 'Features',
              contact: 'Contact',
              login: 'Log in',
              requestDemo: 'Start demo',
            },
            footer: {
              product: 'Product',
              company: 'Company',
              tagline: 'The management platform built for pet stores — inventory, sales, pets, daycare and grooming, all in one place.',
              newsletter: 'Newsletter',
              newsletterText: 'Tips for pet store owners, delivered monthly.',
              emailPlaceholder: 'you@example.com',
              subscribe: 'Subscribe',
              privacy: 'Privacy Policy',
              terms: 'Terms of Service',
              rights: 'All rights reserved.',
              pricing: 'Pricing',
            },
            home: {
              title: 'Waggy — All-in-One Pet Store & Veterinary Management Software',
              description: 'Waggy is all-in-one pet store and veterinary management software to run inventory, sales, pet records, daycare, and grooming from one dashboard.',
            },
            hero: {
              eyebrow: 'Built for the people behind every pet shop',
              headlinePre: 'Pet store management software for',
              headlineHighlight: 'inventory, sales, daycare and grooming',
              sub: 'Inventory, sales, pet records, daycare and grooming — everything your store needs in one place. Designed for the people who care about animals as much as they care about their business.',
              cta: 'Start demo',
              login: 'Log in',
              stat1Label: 'Products in stock',
              stat2Label: 'Orders this month',
              stat3Label: 'Pet families',
            },
            stats: {
              stores: 'Stores running Waggy',
              orders: 'Orders processed',
              modules: 'Operational modules',
              uptime: 'Uptime',
            },
            features: {
              eyebrow: 'Everything your store needs',
              heading: 'One home for your entire business',
              sub: 'From kennels to kibble, from paw prints to invoices — Waggy brings every part of your store together in one simple platform.',
              learnMore: 'Learn more →',
              inventory: { title: 'Inventory', desc: 'Track stock, manage barcodes and SKUs, import products in bulk, and never run out of what your customers need.' },
              sales: { title: 'Sales & Invoicing', desc: 'Create sales, generate invoices, and keep a complete record of every transaction.' },
              pets: { title: 'Pets & Customers', desc: 'Maintain pet profiles linked to their owners and track purchase and service history.' },
              daycare: { title: 'Daycare', desc: 'Manage daycare bookings, check-ins, and scheduling for dogs and cats.' },
              grooming: { title: 'Grooming', desc: 'Schedule grooming appointments, track services, and manage grooming history per pet.' },
              users: { title: 'Your team', desc: 'Invite your team members and control who can see and do what in your store.' },
            },
            how: {
              eyebrow: 'Getting started',
              heading: 'Up and running in 3 steps',
              signup: { title: 'Create your account', desc: 'Sign up and get access to your Waggy workspace in minutes.' },
              configure: { title: 'Configure your store', desc: 'Import your product catalogue, set up categories, and customise your preferences.' },
              operate: { title: 'Start operating', desc: 'Process sales, manage pets, and track every service from your unified dashboard.' },
            },
            testimonials: {
              eyebrow: 'What pet store owners are saying',
              heading: 'Trusted by people who love animals',
              a: { quote: '"Waggy transformed how we manage our store. Inventory and sales in one place — it\'s a game changer."' },
              b: { quote: '"The daycare and grooming modules saved us hours every week. Our team loves it."' },
              c: { quote: '"Finally a platform that truly understands the pet business. Easy to set up and great support."' },
            },
            faq: {
              eyebrow: 'Frequently asked questions',
              heading: 'Got questions?',
              q1: { q: 'What is Waggy?', a: 'Waggy is an all-in-one management platform designed specifically for pet product stores. It covers inventory, sales, invoicing, pet profiles, daycare and grooming in a single place.' },
              q2: { q: 'Is Waggy cloud-based?', a: 'Yes. Waggy runs in the cloud, so you can access it from any device with a browser — no installation required.' },
              q3: { q: 'Can I manage multiple stores?', a: 'Yes. Waggy is designed to support multiple stores independently. Each store has its own isolated workspace with its own data and settings.' },
              q4: { q: 'How do I get started?', a: 'Request a demo through our contact page and our team will reach out to get you set up.' },
              q5: { q: 'Is my data secure?', a: 'Yes. All data is encrypted in transit and at rest. We follow industry-standard security practices.' },
            },
            cta: {
              heading: 'Ready to spend more time with the animals?',
              sub: 'Join pet shops already running on Waggy. Get started in minutes.',
              primary: 'Request a demo',
              secondary: 'Log in',
            },
            featuresPage: {
              title: 'Features — Waggy',
              description: 'Explore Waggy features: inventory, sales, pets, daycare, grooming, and multi-user management.',
              heading: 'Features',
              placeholder: 'Detailed feature documentation coming soon.',
            },
            contactPage: {
              title: 'Contact — Waggy',
              description: 'Get in touch with the Waggy team to request a demo or ask any questions.',
              heading: 'Contact us',
              placeholder: 'A contact form and demo request form are coming soon. In the meantime, reach out to us at adelavina@qubbio.com.',
            },
            pricingPage: {
              title: 'Pricing — Waggy',
              description: 'Waggy pricing plans for pet stores: Starter, Growth and Pro. Monthly or annual billing with a 15% discount.',
              eyebrow: 'Plans',
              heading: 'Simple pricing to grow',
              sub: 'No hidden fees and no location-based pricing. Plan value is based on capability.',
              basePriceNote: 'Base monthly price: {{basePrice}} · Annual billing includes {{discount}}% discount',
              trialNote: 'Your first 14 days are free on every plan.',
              noCardNote: 'No card details are required to get started.',
              annualLabel: 'Yearly:',
              monthUnit: 'month',
              yearUnit: 'year',
              cta: 'Start demo',
              tiers: {
                starter: {
                  name: 'Starter',
                  features: ['Single user', 'Basic modules', 'Email support'],
                },
                growth: {
                  name: 'Growth',
                  features: ['Multi-user', 'Advanced reports', 'Contact-us forms', 'Appointment-scheduling forms'],
                },
                pro: {
                  name: 'Pro',
                  features: ['Priority support', 'API access', 'Advanced integrations'],
                },
              },
            },
            onboarding: {
              registerTitle: 'Register your business — Waggy',
              registerDescription: 'Create your Waggy business and admin account. Email OTP verification is required before access is enabled.',
              verifyTitle: 'Verify email OTP — Waggy',
              verifyDescription: 'Verify your business admin email to activate your Waggy workspace and access your private area.',
              verifyPhoneTitle: 'Verify phone OTP — Waggy',
              verifyPhoneDescription: 'Verify your business billing phone to complete onboarding and activate your Waggy workspace.',
              stepRegister: 'Step 1 of 3',
              stepVerify: 'Step 2 of 2',
              stepVerifyEmail: 'Step 2 of 3',
              stepVerifyPhone: 'Step 3 of 3',
              heading: 'Start your business registration',
              subheading: 'We will send a verification code to activate your workspace.',
              verifyHeading: 'Verify your admin email',
              verifySubheading: 'Enter the 6-digit OTP sent to your admin email to continue to phone verification.',
              verifyPhoneHeading: 'Verify your billing phone',
              verifyPhoneSubheading: 'Enter the 6-digit OTP sent by SMS to complete onboarding and activate your business.',
              noCard: 'No card details needed right now',
              tenantName: 'Your business',
              adminName: 'Admin full name',
              adminEmail: 'Admin email',
              adminPassword: 'Admin password',
              adminPasswordHelp: 'Use at least 8 characters with letters, numbers, and special characters.',
              billingLegalName: 'Billing legal name',
              billingEmail: 'Billing email',
              billingPhone: 'Billing phone',
              billingCountryCode: 'Billing country',
              tenantId: 'Business id',
              otpCode: 'OTP code',
              continueToOtp: 'Continue to email verification',
              continueToPhoneVerification: 'Continue to phone verification',
              submitting: 'Creating your business...',
              verifying: 'Verifying code...',
              activateTenant: 'Activate business and continue',
              backToRegister: 'Back to registration',
              otpSentTo: 'Code sent to',
              otpExpiresAt: 'Expires at',
              resendOtp: 'Resend OTP',
              resendOtpIn: 'Resend OTP in {{seconds}}s',
              resending: 'Resending code...',
              resendSuccess: 'A new OTP code has been sent to your email.',
              resendHint: 'Need a new code? You can resend OTP with a 180-second cooldown for security.',
              resendPhoneSuccess: 'A new OTP code has been sent to your phone.',
              resendPhoneHint: 'Need a new SMS code? You can resend OTP with a 180-second cooldown for security.',
              errors: {
                tenantNameRequired: 'Your business name is required.',
                adminNameRequired: 'Admin full name is required.',
                adminEmailRequired: 'Admin email is required.',
                adminPasswordRequired: 'Password is required.',
                adminPasswordComplexity: 'Password must have at least 8 characters, including letters, numbers, and a special character.',
                billingLegalNameRequired: 'Billing legal name is required.',
                billingEmailRequired: 'Billing email is required.',
                billingPhoneRequired: 'Billing phone is required.',
                billingPhoneInvalid: 'Use international format, for example +34987654321.',
                billingCountryRequired: 'Billing country is required.',
                billingCountryInvalid: 'Please choose a valid billing country.',
                tenantIdRequired: 'Business id is required.',
                otpInvalid: 'OTP code must be 6 digits.',
                emailInvalid: 'Please enter a valid email.',
                registerFailed: 'Unable to start registration. Please try again.',
                verifyFailed: 'Unable to verify code. Please try again.',
                verifyPhoneFailed: 'Unable to verify phone OTP. Please try again.',
                authFailed: 'Unable to complete authentication.',
                recaptchaRequired: 'Security check unavailable. Please try again in a few seconds.',
                resendFailed: 'Unable to resend OTP right now.',
                resendPhoneFailed: 'Unable to resend phone OTP right now.'
              }
            },
            privacyPage: {
              title: 'Privacy Policy — Waggy',
              description: 'Read the Waggy privacy policy.',
              heading: 'Privacy Policy',
              placeholder: 'Our privacy policy is being finalised. Please check back soon.',
            },
            termsPage: {
              title: 'Terms of Service — Waggy',
              description: 'Read the Waggy terms of service.',
              heading: 'Terms of Service',
              placeholder: 'Our terms of service are being finalised. Please check back soon.',
            },
          }
        }
      },
      es: {
        translation: {
          unsavedChanges: {
            title: 'Cambios sin guardar',
            message: 'Tienes cambios sin guardar. Si sales ahora se perderán.',
            keepEditing: 'Seguir editando',
            discardAndLeave: 'Descartar cambios y salir',
          },
          common: {
            loading: 'Cargando...',
            error: 'Ocurrió un error',
            save: 'Guardar',
            cancel: 'Cancelar',
            close: 'Cerrar',
            imageUpload: {
              upload: 'Subir imagen',
              change: 'Cambiar imagen',
              remove: 'Eliminar imagen',
              dragDrop: 'Arrastra y suelta una imagen aquí o búscala en tu dispositivo.',
              help: 'La app generará variantes miniatura, tarjeta y detalle en el navegador.',
              optimizing: 'Optimizando imagen...',
              optimized: 'Optimizada: {{name}}',
              previewAlt: 'Vista previa de la imagen',
              placeholder: 'Aún no hay imagen seleccionada',
              variants: 'Miniatura / tarjeta / detalle',
              error: 'No se pudo optimizar la imagen seleccionada.'
            },
            search: 'Buscar',
            actions: 'Acciones',
            delete: 'Eliminar',
            edit: 'Editar',
            create: 'Crear',
            update: 'Actualizar',
            refresh: 'Actualizar',
            add: 'Agregar',
            remove: 'Quitar',
            back: 'Atrás',
            next: 'Siguiente',
            previous: 'Anterior',
            yes: 'Sí',
            no: 'No',
            filter: 'Filtrar',
            clear: 'Limpiar',
            submit: 'Enviar',
            confirm: 'Confirmar',
            noData: 'No hay datos disponibles',
            selectAll: 'Seleccionar Todo',
            optional: 'Opcional',
            required: 'Requerido',
            deleting: 'Eliminando...'
          },
          pagination: {
            show: 'Mostrar',
            itemsPerPage: 'elementos por página',
            showing: 'Mostrando',
            of: 'de'
          },
          navbar: {
            brand: 'Waggy Tools',
            hello: 'Hola',
            login: 'Iniciar Sesión',
            logout: 'Cerrar Sesión',
            admin: 'Admin',
            adminUserManagement: 'Gestión de Usuarios',
            adminContacts: 'Mensajes de Contacto',
            adminPlatformTenants: 'Tenants de Plataforma'
          },
          sidebar: {
            inventory: 'Inventario',
            pets: 'Mascotas',
            sales: 'Ventas',
            invoices: 'Facturas',
            daycare: 'Guardería',
            grooming: 'Peluquería Canina',
            platform: 'Tenants de Plataforma',
            storefrontOrders: 'Pedidos de Tienda',
            storefrontCustomers: 'Clientes de Tienda',
            navigation: 'Navegación',
            toggleMenu: 'Mostrar u ocultar menú de navegación',
            closeMenu: 'Cerrar menú de navegación'
          },
          storefront: {
            status: {
              PENDING_PAYMENT: 'Pendiente de pago',
              PAID: 'Pagado',
              FULFILLED: 'Completado',
              CANCELLED: 'Cancelado',
            },
            paymentStatus: {
              PENDING: 'Pendiente',
              REQUIRES_ACTION: 'Requiere acción',
              PROCESSING: 'Procesando',
              PAID: 'Pagado',
              FAILED: 'Fallido',
              REFUNDED: 'Reembolsado',
            },
            orders: {
              title: 'Pedidos de Tienda',
              subtitle: 'Pedidos realizados a través de la tienda pública',
              orderNumber: 'Pedido',
              customer: 'Cliente',
              status: 'Estado',
              total: 'Total',
              placedAt: 'Fecha',
              allStatuses: 'Todos los estados',
              searchPlaceholder: 'Buscar por número de pedido, email o nombre',
              empty: 'Todavía no hay pedidos de tienda',
              loadFailed: 'No se pudieron cargar los pedidos',
              actionFailed: 'La acción falló',
              viewDetails: 'Ver detalles',
              detailTitle: 'Detalles del pedido',
              items: 'Artículos',
              subtotal: 'Subtotal',
              vat: 'IVA',
              shipping: 'Envío',
              shippingAddress: 'Dirección de envío',
              notes: 'Notas',
              markFulfilled: 'Marcar como completado',
              cancelOrder: 'Cancelar pedido',
              fulfilSuccess: 'Pedido marcado como completado',
              cancelSuccess: 'Pedido cancelado y stock liberado',
              confirmFulfilTitle: '¿Marcar el pedido como completado?',
              confirmFulfilMessage: 'El cliente verá este pedido como completado.',
              confirmCancelTitle: '¿Cancelar este pedido?',
              confirmCancelMessage: 'El stock reservado se devolverá al inventario.',
            },
            customers: {
              title: 'Clientes de Tienda',
              subtitle: 'Cuentas de cliente registradas en la tienda pública',
              name: 'Nombre',
              phone: 'Teléfono',
              orders: 'Pedidos',
              lifetimeValue: 'Valor total',
              status: 'Estado',
              active: 'Activo',
              inactive: 'Inactivo',
              joined: 'Alta',
              searchPlaceholder: 'Buscar por nombre o email',
              empty: 'Todavía no hay clientes de tienda',
              loadFailed: 'No se pudieron cargar los clientes',
            },
          },
          widget: {
            admin: {
              title: 'Solicitudes del Widget',
              subtitle: 'Mensajes de contacto y solicitudes de cita de peluquería desde tu widget público',
              empty: 'Aún no hay solicitudes.',
              statusUpdated: 'Estado actualizado.',
              markRead: 'Marcar como leído',
              markHandled: 'Marcar como gestionado',
              col: {
                type: 'Tipo',
                status: 'Estado',
                name: 'Nombre',
                email: 'Email',
                pet: 'Mascota',
                preferred: 'Fecha / hora preferida',
                message: 'Mensaje',
                date: 'Recibido',
              },
              error: {
                loadFailed: 'Error al cargar las solicitudes.',
                updateFailed: 'Error al actualizar el estado.',
              },
            },
            settings: {
              slugTitle: 'Tu identificador de widget',
              slugDesc: 'Este es tu identificador único en la URL del widget. Comparte el código iframe en tu sitio web para que los clientes puedan contactarte.',
              slugLoading: 'Cargando…',
              embedTitle: 'Código de inserción',
              embedDesc: 'Copia este fragmento y pégalo en cualquier página de tu sitio web.',
              copy: 'Copiar',
              copied: '¡Copiado!',
              previewTitle: 'Vista previa en vivo',
              previewDesc: 'Así verán el widget tus clientes.',
            },
          },
          inventory: {
            title: 'Gestión de Inventario',
            searchPlaceholder: 'Buscar por nombre, SKU, categoría…',
            barCodePlaceholder: 'Buscar por código de barras…',
            showInactive: 'Mostrar Inactivos',
            addProduct: 'Agregar Producto',
            actions: 'Acciones',
            columns: {
              name: 'Nombre',
              sku: 'SKU',
              brand: 'Marca',
              category: 'Categoría',
              stock: 'Stock',
              finalPrice: 'Precio Final',
              status: 'Estado',
              actions: 'Acciones'
            },
            filters: {
              category: 'Categoría',
              allCategories: 'Todas las categorías',
              brand: 'Marca',
              allBrands: 'Todas las marcas'
            },
            alerts: {
              title: 'Alertas',
              empty: 'No hay alertas',
              colName: 'Nombre',
              colStatus: 'Estado'
            },
            purchaseHistory: {
              title: 'Historial de Compras',
              loadFailed: 'Error al cargar el historial de compras',
              addFailed: 'Error al agregar la compra',
              removeFailed: 'Error al eliminar la compra',
              unitPricePlaceholder: 'Nuevo precio',
              supplierPlaceholder: 'Proveedor',
              notesPlaceholder: 'Notas',
              add: 'Agregar compra',
              saving: 'Guardando…',
              loading: 'Cargando…',
              empty: 'No hay compras registradas',
              colDate: 'Fecha',
              colPrice: 'Precio',
              colChange: 'Cambio',
              colSupplier: 'Proveedor',
              colNotes: 'Notas',
              remove: 'Eliminar compra'
            },
            startSale: 'Iniciar Venta',
            viewDetails: 'Ver detalles',
            storefront: 'Tienda',
            published: 'Publicado',
            hidden: 'Oculto',
            publish: 'Publicar en la tienda',
            unpublish: 'Ocultar de la tienda',
            publishSuccess: 'Producto publicado en la tienda',
            unpublishSuccess: 'Producto oculto de la tienda',
            publishFailed: 'No se pudo actualizar la visibilidad en la tienda',
            duplicateProduct: 'Duplicar',
            createProduct: 'Crear Producto',
            editProduct: 'Editar Producto',
            productDetails: 'Detalles del Producto',
            productName: 'Nombre del Producto',
            description: 'Descripción',
            sku: 'SKU',
            barcode: 'Código de Barras',
            brand: 'Marca',
            category: 'Categoría',
            categoryCode: 'Código de Categoría',
            categoryName: 'Nombre de Categoría',
            purchasePrice: 'Precio de Compra',
            sellPrice: 'Precio de Venta',
            stock: 'Stock',
            minStock: 'Stock Mínimo',
            notes: 'Notas',
            active: 'Activo',
            namePlaceholder: 'Ingrese nombre del producto',
            descriptionPlaceholder: 'Ingrese descripción del producto',
            skuPlaceholder: 'SKU del producto',
            barcodePlaceholder: 'Código de barras (opcional)',
            brandPlaceholder: 'Marca (opcional)',
            categoryCodePlaceholder: 'Código de categoría (opcional)',
            categoryNamePlaceholder: 'Nombre de categoría (opcional)',
            purchasePricePlaceholder: '0.00',
            sellPricePlaceholder: '0.00',
            stockPlaceholder: '0',
            minStockPlaceholder: 'Opcional',
            weightPlaceholder: 'Opcional',
            notesPlaceholder: 'Opcional',
            productModal: {
              titleCreate: 'Crear Nuevo Producto',
              titleEdit: 'Editar Producto',
              titleDuplicate: 'Duplicar Producto',
              sections: {
                basic: 'Información Básica',
                pricing: 'Precios',
                inventory: 'Inventario'
              },
              fields: {
                listPrice: 'Precio Base (sin IVA)',
                finalPrice: 'Precio de Venta (PVP, IVA incluido)',
                vatRate: 'Tipo de IVA',
                costPrice: 'Precio de Coste',
                stockQuantity: 'Cantidad en Stock',
                minStockLevel: 'Stock Mínimo',
                weightGrams: 'Peso (g)',
                daycareTime: 'Tiempo de Guardería',
                daycareBundle: 'Bono de Guardería'
              },
              image: {
                label: 'Imagen del Producto',
                help: 'Genera variantes miniatura/tarjeta/detalle en el navegador y las envía en el mismo payload de crear/actualizar.',
                optimizing: 'Optimizando imagen...',
                optimized: 'Optimizada: {{name}}',
                previewAlt: 'Vista previa del producto'
              },
              selectBrand: 'Selecciona marca',
              selectCategory: 'Selecciona categoría',
              selectDaycareTime: 'Selecciona tiempo de guardería',
              actions: {
                saving: 'Guardando...',
                update: 'Actualizar Producto',
                create: 'Crear Producto',
                duplicate: 'Duplicar Producto'
              },
              errors: {
                saveFailed: 'No se pudo guardar el producto',
                unexpected: 'Ocurrió un error inesperado'
              }
            },
            importExport: {
              exportProducts: 'Exportar Productos',
              importProducts: 'Importar Productos',
              exporting: 'Exportando...',
              importing: 'Importando...',
              exportFailed: 'No se pudieron exportar los productos',
              importFailed: 'No se pudieron importar los productos',
              summary: 'Importación completada. Creados: {{created}}, Actualizados: {{updated}}, Omitidos: {{skipped}}, Fallidos: {{failed}}'
            }
          },
          category: {
            confirm: {
              management: 'Gestión de Categorías',
              deleteTitle: 'Eliminar categoría',
              delete: '¿Está seguro que desea eliminar "{{name}}"? Esta acción no se puede deshacer.',
            },
          },
          brand: {
            management: 'Gestión de Marcas',
            title: 'Marcas',
            create: 'Crear Marca',
            edit: 'Editar Marca',
            delete: 'Eliminar Marca',
            field: {
              name: 'Nombre de la Marca',
              description: 'Descripción'
            },
            placeholder: {
              name: 'Ingrese nombre de la marca',
              description: 'Ingrese la descripción de la marca (opcional)'
            },
            empty: 'No hay marcas disponibles',
            confirm: {
              deleteTitle: 'Eliminar marca',
              delete: '¿Está seguro que desea eliminar "{{name}}"? Esta acción no se puede deshacer.'
            },
            error: {
              loadFailed: 'Error al cargar las marcas',
              saveFailed: 'Error al guardar la marca',
              deleteFailed: 'Error al eliminar la marca',
              nameRequired: 'El nombre de la marca es requerido',
              nameTooLong: 'El nombre de la marca no puede exceder 100 caracteres',
              descriptionTooLong: 'La descripción de la marca no puede exceder 500 caracteres'
            },
            success: {
              created: 'Marca creada exitosamente',
              updated: 'Marca actualizada exitosamente',
              deleted: 'Marca eliminada exitosamente'
            }
          },
          productGroup: {
            management: 'Variaciones',
            title: 'Variaciones de Producto',
            create: 'Crear Grupo de Variaciones',
            edit: 'Editar Grupo de Variaciones',
            empty: 'Aún no hay grupos de variaciones. Agrupa productos para venderlos en una sola tarjeta con opciones.',
            loadFailed: 'Error al cargar los grupos de variaciones',
            saveFailed: 'Error al guardar el grupo de variaciones',
            deleteFailed: 'Error al eliminar el grupo de variaciones',
            defaultVariation: 'Predeterminada',
            confirm: {
              deleteTitle: 'Eliminar grupo de variaciones',
              delete: '¿Eliminar el grupo de variaciones {{name}}? Los productos se conservan y vuelven a venderse por separado.'
            },
            field: {
              name: 'Nombre del Grupo',
              description: 'Descripción',
              isActive: 'Activo',
              slug: 'URL de la tienda',
              priceRange: 'Rango de precios',
              totalStock: 'Stock total',
              productCount: 'Variaciones',
              attributeCode: 'Código',
              attributeName: 'Nombre de la opción',
              valueCode: 'Código',
              valueName: 'Valor',
              hexColor: 'Color de muestra',
              product: 'Producto',
              productSku: 'SKU',
              productPrice: 'Precio',
              productStock: 'Stock'
            },
            placeholder: {
              name: 'ej. Arnés Clásico Dukier',
              description: 'Descripción opcional mostrada en la tienda',
              attributeName: 'ej. Tamaño, Color',
              attributeCode: 'ej. tamano',
              valueName: 'ej. S, M, L, Azul',
              valueCode: 'ej. s, m, l, azul',
              product: 'Selecciona un producto'
            },
            search: {
              product: 'Buscar por nombre, SKU, categoría…',
              noProductsFound: 'Ningún producto coincide',
              inOtherGroup: 'En el grupo {{group}}',
              inThisGroup: 'Ya está en este grupo',
              clearProduct: 'Quitar producto'
            },
            section: {
              details: 'Detalles del grupo',
              attributes: 'Opciones',
              members: 'Variaciones'
            },
            action: {
              addAttribute: 'Añadir opción',
              addValue: 'Añadir valor',
              addMember: 'Añadir variación',
              removeAttribute: 'Quitar opción',
              removeValue: 'Quitar valor',
              removeMember: 'Quitar variación',
              setDefault: 'Marcar como predeterminada'
            },
            helper: {
              attributes: 'Define los tipos de opción que elige el cliente, como tamaño o color. Cada variación selecciona un valor por opción.',
              members: 'Cada variación es un producto real con su propio SKU, código de barras, precio, stock e imágenes. Un producto puede pertenecer a un solo grupo.',
              codes: 'Los códigos se envían de forma explícita. Se rellenan a partir del nombre; cámbialos si necesitas otro valor.',
              priceRange: '{{min}} – {{max}}',
              singlePrice: '{{price}}'
            },
            error: {
              nameRequired: 'El nombre del grupo es requerido',
              noMembers: 'El grupo debe tener al menos una variación',
              attributeNameRequired: 'Cada opción necesita un nombre',
              attributeCodeRequired: 'Dale un nombre a la opción para poder rellenar su código',
              attributeValueRequired: 'Cada opción necesita al menos un valor',
              valueNameRequired: 'Cada valor necesita un nombre',
              duplicateValueCode: 'Código de valor duplicado "{{code}}"',
              duplicateAttributeCode: 'Código de opción duplicado "{{code}}"',
              invalidHexColor: '"{{color}}" no es un color hexadecimal válido',
              productRequired: 'Cada variación necesita un producto',
              duplicateProduct: 'El mismo producto aparece más de una vez',
              missingSelection: 'La variación {{index}} no tiene valor para {{attribute}}',
              unknownSelection: '"{{value}}" no es un valor válido para {{attribute}}',
              defaultNotMember: 'La variación predeterminada debe ser una de las variaciones del grupo'
            }
          },
          pets: {
            title: 'Gestión de Mascotas',
            createPet: 'Dar de Alta Mascota',
            editPet: 'Editar Mascota',
            deletePet: 'Eliminar Mascota',
            modal: {
              titleCreate: 'Crear Nueva Mascota',
              titleEdit: 'Editar Mascota',
              sections: {
                petInfo: 'Información de la Mascota',
                ownerInfo: 'Información del Dueño'
              },
              selectCustomer: 'Selecciona Cliente',
              selectCustomerPlaceholder: '-- Selecciona un cliente --',
              selectExistingCustomer: 'Seleccionar Cliente Existente',
              createNewCustomer: 'Crear Nuevo Cliente',
              actions: {
                saving: 'Guardando...',
                update: 'Actualizar Mascota'
              },
              errors: {
                saveFailed: 'No se pudo guardar la mascota',
                unexpected: 'Ocurrió un error inesperado',
                createCustomerFailed: 'No se pudo crear el cliente',
                customerRequired: 'Selecciona un cliente o crea uno nuevo'
              }
            },
            image: {
              label: 'Imagen de la Mascota',
              help: 'Sube una foto de la mascota y la app generará las mismas variantes miniatura, tarjeta y detalle usadas por los productos.',
              previewAlt: 'Vista previa de la mascota'
            },
            viewDetails: 'Ver Detalles',
            petDetails: 'Detalles de la Mascota',
            petName: 'Nombre de la Mascota',
            breed: 'Raza',
            birthDate: 'Fecha de Nacimiento',
            gender: 'Género',
            weight: 'Peso',
            color: 'Color',
            notes: 'Notas',
            owner: 'Dueño',
            customerInfo: 'Información del Cliente',
            customerName: 'Nombre del Cliente',
            email: 'Correo Electrónico',
            phone: 'Teléfono',
            address: 'Dirección',
            namePlaceholder: 'Ingrese nombre de la mascota',
            breedPlaceholder: 'Ingrese la raza de la mascota',
            customerNamePlaceholder: 'Ingrese nombre del cliente',
            emailPlaceholder: 'cliente@ejemplo.com',
            phonePlaceholder: '+1 234 567 8900',
            addressPlaceholder: 'Dirección completa',
            searchPlaceholder: 'Buscar por nombre de mascota, raza o dueño…',
            empty: 'No se encontraron mascotas',
            selector: {
              selectExisting: 'Seleccionar Mascota Existente',
              onboardNew: 'Dar de Alta Nueva Mascota',
              existingOwner: 'Dueño Existente',
              newOwner: 'Nuevo Dueño',
              selectOwner: 'Seleccionar Dueño',
              ownerNameLabel: 'Nombre del Dueño'
            },
            quickAddPet: 'Agregar Mascota Rápido',
            petNameQuick: 'ej. Max',
            breedQuick: 'ej. Golden Retriever',
            ownerNamePlaceholder: 'Nombre completo',
            ownerEmailPlaceholder: 'dueño@ejemplo.com',
            ownerPhonePlaceholder: '+1 234 567 8900',
            ownerAddressPlaceholder: 'Dirección completa',
            detailsModal: {
              inDaycareSince: 'En guardería desde {{time}}',
              loadingHistory: 'Cargando historial de compras…',
              emptyHistory: 'No hay compras registradas para {{petName}} todavía.',
              loadingDaycare: 'Cargando saldo de guardería…',
              emptyDaycare: 'Todavía no hay horas de guardería compradas.',
              remainingUnits: 'Unidades de guardería restantes',
              daycareSessionsTitle: 'Sesiones de guardería para este perro',
              daycareSessionsEmpty: 'No hay sesiones de guardería registradas para este perro todavía.',
              daycareSessionFallback: 'Sesión de guardería',
              inProgress: 'En curso',
              sections: {
                topProducts: 'Productos Más Comprados',
                daycareHours: 'Horas de Guardería',
                orderHistory: 'Historial de Pedidos',
                sessionSummary: 'Resumen de Sesión'
              },
              stats: {
                totalOrders: 'Pedidos Totales',
                totalSpent: 'Total Gastado',
                avgOrder: 'Promedio por Pedido',
                uniqueProducts: 'Productos Únicos'
              },
              labels: {
                checkIn: 'Entrada',
                checkOut: 'Salida',
                entry: 'Entrada',
                exit: 'Salida',
                duration: 'Duración',
                hoursToDeduct: 'Horas a descontar',
                balanceAfterSession: 'Saldo después de la sesión'
              },
              status: {
                active: 'ACTIVA',
                completed: 'COMPLETADA'
              },
              itemLabel: {
                singular: 'artículo',
                plural: 'artículos'
              },
              table: {
                product: 'Producto',
                unitPrice: 'Precio Unitario',
                qty: 'Cant.',
                subtotal: 'Subtotal'
              },
              actions: {
                confirmEntryTime: '🐾 Confirmar Hora de Entrada',
                confirmExitTime: '🏠 Confirmar Hora de Salida',
                confirmEntry: 'Confirmar Entrada',
                confirmExit: 'Confirmar Salida',
                reset: 'Restablecer',
                recording: 'Registrando…',
                recordCheckout: 'Registrar Salida',
                dogEntry: 'Entrada del Perro',
                dogExit: 'Salida del Perro'
              },
              summary: {
                sufficientBalance: '{{petName}} tiene saldo suficiente. Quedarán {{remaining}} después de esta sesión.',
                insufficientBalance: 'Saldo insuficiente. Esto dejará a {{petName}} con un sobregiro de {{overdraft}}.'
              },
              errors: {
                loadHistoryFailed: 'Error al cargar el historial de compras',
                checkInFailed: 'Error al registrar la entrada',
                checkOutFailed: 'Error al registrar la salida',
                invalidExitTime: 'La hora de salida debe ser posterior a la hora de entrada. Corrige los horarios.'
              }
            }
          },
          sales: {
            title: 'Ventas',
            subtitle: 'Todas las órdenes ordenadas por venta más reciente',
            invoices: {
              title: 'Facturas',
              subtitle: 'Facturas emitidas y exportaciones',
              export: 'Exportar Excel',
              exporting: 'Exportando…',
              empty: 'No se encontraron facturas',
              colNumber: 'Nº de Factura',
              colIssuedAt: 'Emitida',
              colCustomer: 'Cliente',
              colPet: 'Mascota'
            },
            newSale: 'Nueva Venta',
            createSale: 'Crear Venta',
            orderDate: 'Fecha de Orden',
            total: 'Total',
            status: 'Estado',
            items: 'Artículos',
            customer: 'Cliente',
            selectPet: 'Seleccionar Mascota',
            selectProduct: 'Seleccionar Producto',
            quantity: 'Cantidad',
            price: 'Precio',
            addItem: 'Agregar Artículo',
            searchProductPlaceholder: 'Buscar por nombre, SKU, categoría…',
            modal: {
              editDraft: 'Editar Orden Borrador',
              loading: 'Cargando datos...',
              addProducts: '2. Agregar Productos',
              orderSummary: '3. Resumen del Pedido',
              emptyCart: 'Aún no se han agregado productos.',
              product: 'Producto',
              unitPrice: 'Precio Unitario',
              qty: 'Cant.',
              subtotal: 'Subtotal',
              cancel: 'Cancelar',
              saving: 'Guardando…',
              saveDraft: 'Guardar Borrador',
              completeOrder: 'Completar Orden',
              confirmSale: 'Confirmar Venta'
            }
          },
          daycare: {
            title: 'Guardería',
            subtitle: 'Los perros actualmente en guardería se muestran primero, luego ordenados por fecha de salida.',
            checkIn: 'Entrada',
            checkOut: 'Salida',
            openSessions: 'Sesiones Abiertas',
            completedToday: 'Completadas Hoy',
            totalSessions: 'Sesiones Totales',
            pet: 'Mascota',
            owner: 'Dueño',
            checkInTime: 'Entrada',
            checkOutTime: 'Salida',
            duration: 'Duración',
            status: 'Estado',
            actions: 'Acciones'
          },
          grooming: {
            title: 'Peluquería Canina',
            subtitle: 'Programa y gestiona citas de peluquería para mascotas.',
            newAppointment: 'Nueva Cita',
            createAppointment: 'Crear Cita',
            editAppointment: 'Editar Cita',
            deleteAppointment: 'Eliminar Cita',
            pet: 'Mascota',
            owner: 'Dueño',
            date: 'Fecha',
            time: 'Hora',
            service: 'Servicio',
            status: 'Estado',
            notes: 'Notas',
            notesPlaceholder: 'Notas opcionales sobre la cita...',
            scheduledDateTime: 'Fecha y Hora Programada',
            saveChanges: 'Guardar Cambios',
            saving: 'Guardando...',
            creating: 'Creando...',
            previousDay: 'Día anterior',
            nextDay: 'Día siguiente',
            clearFilter: 'Limpiar filtro',
            edit: 'Editar',
            delete: 'Eliminar',
            allStatuses: 'Todos los Estados',
            scheduled: 'Programada',
            completed: 'Completada',
            cancelled: 'Cancelada'
          },
          footer: {
            language: 'Idioma',
            english: 'English',
            spanish: 'Español'
          },
          login: {
            title: 'Iniciar Sesión',
            email: 'Correo Electrónico',
            password: 'Contraseña',
            emailPlaceholder: 'tu@correo.com',
            passwordPlaceholder: 'Mínimo 6 caracteres',
            loginButton: 'Iniciar Sesión',
            loginError: 'Inicio de sesión fallido. Por favor verifica tus credenciales.'
          },
          admin: {
            users: {
              pageTitle: 'Gestión de Usuarios',
              createUser: 'Crear Usuario',
              editUser: 'Editar Usuario',
              name: 'Nombre',
              email: 'Email',
              tenant: 'Tenant',
              roles: 'Roles',
              status: 'Estado',
              actions: 'Acciones',
              allTenants: 'Todos los tenants',
              tenantFilter: 'Filtrar por tenant',
              superAdminScope: 'Gestión de usuarios cross-tenant habilitada',
              loadTenantsError: 'Error al cargar los tenants.',
              active: 'Activo',
              inactive: 'Inactivo',
              edit: 'Editar',
              deactivate: 'Desactivar',
              noUsers: 'No se encontraron usuarios.',
              loadError: 'Error al cargar los usuarios.',
              created: 'Usuario creado.',
              updated: 'Usuario actualizado.',
              deactivated: 'Usuario desactivado.',
              deactivateError: 'Error al desactivar el usuario.',
              form: {
                name: 'Nombre',
                email: 'Email',
                password: 'Contraseña',
                tenant: 'Tenant',
                selectTenant: 'Seleccionar tenant',
                roles: 'Roles',
                active: 'Activo',
                nameRequired: 'El nombre es requerido.',
                emailRequired: 'El email es requerido.',
                passwordRequired: 'La contraseña es requerida.',
                tenantRequired: 'El tenant es obligatorio.',
                rolesRequired: 'Selecciona al menos un rol.',
                error: 'Ocurrió un error. Por favor intenta de nuevo.'
              }
            }
          },
          public: {
            nav: {
              features: 'Funcionalidades',
              contact: 'Contacto',
              login: 'Ingresar',
              requestDemo: 'Comenzar Demo',
            },
            footer: {
              product: 'Producto',
              company: 'Empresa',
              tagline: 'El software de gestión para tiendas de animales — inventario, ventas, mascotas, guardería y peluquería canina en un solo lugar.',
              newsletter: 'Newsletter',
              newsletterText: 'Consejos para dueños de tiendas de mascotas, cada mes.',
              emailPlaceholder: 'tu@correo.com',
              subscribe: 'Suscribirse',
              privacy: 'Política de Privacidad',
              terms: 'Términos de Servicio',
              rights: 'Todos los derechos reservados.',
              pricing: 'Precios',
            },
            home: {
              title: 'Waggy — Software integral de gestión para tiendas de animales y veterinarias',
              description: 'Waggy es un software integral para tiendas de animales y equipos veterinarios para gestionar inventario, ventas, fichas de mascotas, guardería y peluquería canina desde un único panel.',
            },
            hero: {
              eyebrow: 'Hecho para quienes cuidan cada mascota',
              headlinePre: 'Software de gestión para tiendas de animales con',
              headlineHighlight: 'inventario, ventas, guardería y peluquería',
              sub: 'Inventario, ventas, fichas de mascotas, guardería y peluquería canina — todo lo que tu tienda necesita en un solo lugar. Pensado para las personas que se preocupan tanto por los animales como por su negocio.',
              cta: 'Comenzar Demo',
              login: 'Ingresar',
              stat1Label: 'Productos en stock',
              stat2Label: 'Órdenes este mes',
              stat3Label: 'Familias con mascota',
            },
            stats: {
              stores: 'Tiendas usando Waggy',
              orders: 'Órdenes procesadas',
              modules: 'Módulos operativos',
              uptime: 'Disponibilidad',
            },
            features: {
              eyebrow: 'Todo lo que tu tienda necesita',
              heading: 'Un solo lugar para todo tu negocio',
              sub: 'De las croquetas a las citas, de los peines a las facturas — Waggy reúne cada parte de tu tienda en una plataforma sencilla.',
              learnMore: 'Saber más →',
              inventory: { title: 'Inventario', desc: 'Controla el stock, gestiona códigos de barras y SKUs, importa productos masivamente.' },
              sales: { title: 'Ventas y Facturación', desc: 'Crea ventas, genera facturas y lleva un registro completo de cada transacción.' },
              pets: { title: 'Mascotas y Clientes', desc: 'Mantén perfiles de mascotas vinculados a sus dueños y seguimiento de historial.' },
              daycare: { title: 'Guardería', desc: 'Gestiona reservas de guardería, check-ins y programación para perros y gatos.' },
              grooming: { title: 'Peluquería Canina', desc: 'Agenda citas de peluquería canina, registra los servicios realizados y consulta el historial completo de cada mascota.' },
              users: { title: 'Tu equipo', desc: 'Invita a los miembros de tu equipo y define quién puede ver y hacer qué en tu tienda.' },
            },
            how: {
              eyebrow: 'Primeros pasos',
              heading: 'En marcha en 3 pasos',
              signup: { title: 'Crea tu cuenta', desc: 'Regístrate y accede a tu espacio de trabajo en minutos.' },
              configure: { title: 'Configura tu tienda', desc: 'Importa tu catálogo, crea categorías y personaliza tus preferencias.' },
              operate: { title: 'Empieza a operar', desc: 'Procesa ventas, gestiona mascotas y registra cada servicio desde tu panel.' },
            },
            testimonials: {
              eyebrow: 'Lo que dicen nuestros clientes',
              heading: 'Lo que dicen quienes cuidan animales',
              a: { quote: '"Waggy transformó la forma en que gestionamos nuestra tienda. Inventario y ventas en un solo lugar."' },
              b: { quote: '"Los módulos de guardería y peluquería canina nos ahorraron horas cada semana. Nuestro equipo lo ama."' },
              c: { quote: '"Por fin una plataforma que de verdad entiende el negocio de las mascotas. Fácil de configurar y con un soporte excelente."' },
            },
            faq: {
              eyebrow: 'Preguntas frecuentes',
              heading: '¿Tienes dudas?',
              q1: { q: '¿Qué es Waggy?', a: 'Waggy es un software de gestión integral para tiendas de animales. Cubre inventario, ventas, facturación, fichas de mascotas, guardería y peluquería canina en un solo lugar.' },
              q2: { q: '¿Es Waggy basado en la nube?', a: 'Sí. Waggy funciona en la nube, por lo que puedes acceder desde cualquier dispositivo con un navegador, sin instalación.' },
              q3: { q: '¿Puedo gestionar varias tiendas?', a: 'Sí. Waggy está diseñado para soportar varias tiendas de forma independiente. Cada tienda tiene su propio espacio aislado, con sus datos y configuración propios.' },
              q4: { q: '¿Cómo empiezo?', a: 'Solicita una demo a través de nuestra página de contacto y nuestro equipo se pondrá en contacto contigo.' },
              q5: { q: '¿Mis datos están seguros?', a: 'Sí. Todos los datos están cifrados en tránsito y en reposo. Seguimos las mejores prácticas de seguridad de la industria.' },
            },
            cta: {
              heading: '¿Listo para dedicar más tiempo a los animales?',
              sub: 'Únete a las tiendas que ya usan Waggy. Empieza en minutos.',
              primary: 'Solicitar demo',
              secondary: 'Ingresar',
            },
            featuresPage: {
              title: 'Funcionalidades — Waggy',
              description: 'Descubre los módulos de Waggy: inventario, ventas, mascotas, guardería y peluquería canina para tu tienda de animales.',
              heading: 'Funcionalidades',
              placeholder: 'Documentación detallada de funcionalidades próximamente.',
            },
            contactPage: {
              title: 'Contacto — Waggy',
              description: 'Ponte en contacto con el equipo de Waggy para solicitar una demo o hacer preguntas.',
              heading: 'Contáctanos',
              placeholder: 'Un formulario de contacto y solicitud de demo estará disponible próximamente. Por ahora, escríbenos a adelavina@qubbio.com.',
            },
            pricingPage: {
              title: 'Precios — Waggy',
              description: 'Planes de Waggy para tiendas de animales: Inicio, Crecimiento y Pro. Facturación mensual o anual con 15% de descuento.',
              eyebrow: 'Planes',
              heading: 'Precios simples para crecer',
              sub: 'Sin costes ocultos y sin límite por ubicación. El precio se define por capacidad del plan.',
              basePriceNote: 'Precio base mensual: {{basePrice}} · Facturación anual con {{discount}}% de descuento',
              trialNote: 'Los primeros 14 días son gratis en todos los planes.',
              noCardNote: 'No necesitas datos de tarjeta para comenzar.',
              annualLabel: 'Anual:',
              monthUnit: 'mes',
              yearUnit: 'año',
              cta: 'Comenzar Demo',
              tiers: {
                starter: {
                  name: 'Inicio',
                  features: ['Usuario único', 'Módulos básicos', 'Soporte por email'],
                },
                growth: {
                  name: 'Crecimiento',
                  features: ['Multiusuario', 'Reportes avanzados', 'Formularios de contacto', 'Formularios para agendar citas'],
                },
                pro: {
                  name: 'Pro',
                  features: ['Soporte prioritario', 'Acceso a API', 'Integraciones avanzadas'],
                },
              },
            },
            onboarding: {
              registerTitle: 'Registro de tu emprendimiento — Waggy',
              registerDescription: 'Crea tu emprendimiento y usuario administrador en Waggy. La verificación por OTP de email es obligatoria antes del acceso.',
              verifyTitle: 'Verificar OTP por email — Waggy',
              verifyDescription: 'Verifica el email del administrador para activar tu emprendimiento y acceder al área privada.',
              verifyPhoneTitle: 'Verificar OTP por teléfono — Waggy',
              verifyPhoneDescription: 'Verifica el teléfono de facturación para completar el onboarding y activar tu espacio en Waggy.',
              stepRegister: 'Paso 1 de 3',
              stepVerify: 'Paso 2 de 2',
              stepVerifyEmail: 'Paso 2 de 3',
              stepVerifyPhone: 'Paso 3 de 3',
              heading: 'Inicia el registro de tu emprendimiento',
              subheading: 'Te enviaremos un código de verificación para activar tu espacio.',
              verifyHeading: 'Verifica el email del administrador',
              verifySubheading: 'Introduce el OTP de 6 dígitos enviado al email del administrador para continuar a la verificación del teléfono.',
              verifyPhoneHeading: 'Verifica tu teléfono de facturación',
              verifyPhoneSubheading: 'Introduce el OTP de 6 dígitos enviado por SMS para completar el onboarding y activar tu emprendimiento.',
              noCard: 'No se requieren datos del medio de pago para comenzar',
              tenantName: 'Tu emprendimiento',
              adminName: 'Nombre completo del administrador',
              adminEmail: 'Email del administrador',
              adminPassword: 'Contraseña del administrador',
              adminPasswordHelp: 'Usa al menos 8 caracteres con letras, números y caracteres especiales.',
              billingLegalName: 'Razón social de facturación',
              billingEmail: 'Email de facturación',
              billingPhone: 'Teléfono de facturación',
              billingCountryCode: 'País de facturación',
              tenantId: 'Id del emprendimiento',
              otpCode: 'Código OTP',
              continueToOtp: 'Continuar a verificación por email',
              continueToPhoneVerification: 'Continuar a verificación por teléfono',
              submitting: 'Creando tu emprendimiento...',
              verifying: 'Verificando código...',
              activateTenant: 'Activar emprendimiento y continuar',
              backToRegister: 'Volver al registro',
              otpSentTo: 'Código enviado a',
              otpExpiresAt: 'Expira en',
              resendOtp: 'Reenviar OTP',
              resendOtpIn: 'Reenviar OTP en {{seconds}}s',
              resending: 'Reenviando código...',
              resendSuccess: 'Se envió un nuevo código OTP a tu email.',
              resendHint: '¿Necesitas un nuevo código?',
              resendPhoneSuccess: 'Se envió un nuevo código OTP a tu teléfono.',
              resendPhoneHint: '¿Necesitas un nuevo SMS?',
              errors: {
                tenantNameRequired: 'El nombre de tu emprendimiento es obligatorio.',
                adminNameRequired: 'El nombre completo del administrador es obligatorio.',
                adminEmailRequired: 'El email del administrador es obligatorio.',
                adminPasswordRequired: 'La contraseña es obligatoria.',
                adminPasswordComplexity: 'La contraseña debe tener al menos 8 caracteres, incluyendo letras, números y un carácter especial.',
                billingLegalNameRequired: 'La razón social de facturación es obligatoria.',
                billingEmailRequired: 'El email de facturación es obligatorio.',
                billingPhoneRequired: 'El teléfono de facturación es obligatorio.',
                billingPhoneInvalid: 'Usa formato internacional, por ejemplo +34987654321.',
                billingCountryRequired: 'El país de facturación es obligatorio.',
                billingCountryInvalid: 'Selecciona un país de facturación válido.',
                tenantIdRequired: 'El id del emprendimiento es obligatorio.',
                otpInvalid: 'El código OTP debe tener 6 dígitos.',
                emailInvalid: 'Introduce un email válido.',
                registerFailed: 'No se pudo iniciar el registro. Inténtalo de nuevo.',
                verifyFailed: 'No se pudo verificar el código. Inténtalo de nuevo.',
                verifyPhoneFailed: 'No se pudo verificar el OTP del teléfono. Inténtalo de nuevo.',
                authFailed: 'No se pudo completar la autenticación.',
                recaptchaRequired: 'La verificación de seguridad no está disponible. Inténtalo de nuevo en unos segundos.',
                resendFailed: 'No se pudo reenviar el OTP en este momento.',
                resendPhoneFailed: 'No se pudo reenviar el OTP por teléfono en este momento.'
              }
            },
            privacyPage: {
              title: 'Política de Privacidad — Waggy',
              description: 'Lee la política de privacidad de Waggy.',
              heading: 'Política de Privacidad',
              placeholder: 'Nuestra política de privacidad está siendo finalizada. Vuelve pronto.',
            },
            termsPage: {
              title: 'Términos de Servicio — Waggy',
              description: 'Lee los términos de servicio de Waggy.',
              heading: 'Términos de Servicio',
              placeholder: 'Nuestros términos de servicio están siendo finalizados. Vuelve pronto.',
            },
          }
        }
      }
    },
    detection: {
      // Order and from where user language should be detected.
      // Without 'navigator', first-time visitors (no saved choice) get the
      // fallback language (Spanish); a previously saved choice is honored.
      order: ['cookie', 'localStorage'],
      // Keys or params to lookup language from
      lookupCookie: 'i18nextLng',
      lookupLocalStorage: 'i18nextLng',
      // Cache user language on
      caches: ['cookie'],
      cookieMinutes: 525600, // 365 days
      cookieOptions: { path: '/', sameSite: 'strict' }
    }
  });

export default i18n;
